import { FormControl, FormControlLabel, Grid, RadioGroup, Paper, Switch, Stack, Typography } from '@mui/material';
import { t } from 'i18next';
import React, { useState } from 'react';
import CustomFormLabel from 'src/components/forms/theme-elements/CustomFormLabel';
import CustomRadio from 'src/components/forms/theme-elements/CustomRadio';
import CustomTextField from 'src/components/forms/theme-elements/CustomTextField';
import { TournamentFormValues } from '../../types';
import { FormikProps } from 'formik';
import { FederationsPermissions } from 'src/types/apps/permissions';
import { usePermissions } from 'src/hooks/usePermissions';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, ContentState, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { useTheme } from '@mui/material/styles';

const AdditionalInfo = ({ formik }: { formik: FormikProps<TournamentFormValues> }) => {
  const isFederation = usePermissions(FederationsPermissions.TOURNAMENTS_VIEW);
  const [useHtmlEditor, setUseHtmlEditor] = useState(false);
  const theme = useTheme();
  const borderColor = theme.palette.divider;

  // Initialize editor state from HTML
  const getEditorStateFromHtml = (html: string) => {
    if (!html) {
      return EditorState.createEmpty();
    }
    const contentBlock = htmlToDraft(html);
    if (contentBlock) {
      const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
      return EditorState.createWithContent(contentState);
    }
    return EditorState.createEmpty();
  };

  const [editorState, setEditorState] = useState(() =>
    getEditorStateFromHtml(formik.values.additional_info || '')
  );

  // Update editor when formik value changes externally
  React.useEffect(() => {
    const currentHtml = draftToHtml(convertToRaw(editorState.getCurrentContent()));
    const formikHtml = formik.values.additional_info || '';

    // Only update if values are different and editor is not focused
    if (currentHtml !== formikHtml && formikHtml !== '') {
      setEditorState(getEditorStateFromHtml(formikHtml));
    }
  }, [formik.values.additional_info]);

  const onEditorStateChange = (newEditorState: EditorState) => {
    setEditorState(newEditorState);
    const html = draftToHtml(convertToRaw(newEditorState.getCurrentContent()));
    // Clean empty content
    const cleanValue = html.trim() === '<p></p>\n' || html.trim() === '' ? '' : html;
    formik.setFieldValue('additional_info', cleanValue);
  };

  console.log(formik.values.additional_info)

  return (
    <>
      <Grid item xs={12}>
        <CustomFormLabel htmlFor="prizes">{t('TournamentsScreen.prizes')}</CustomFormLabel>
        <CustomTextField
          id="prizes"
          fullWidth
          {...formik.getFieldProps('prizes')}
          error={formik.touched.prizes && Boolean(formik.errors.prizes)}
          helperText={formik.touched.prizes && formik.errors.prizes}
        />
      </Grid>
      <Grid item xs={12}>
        <CustomFormLabel htmlFor="daily_timings">
          {t('TournamentsScreen.dailyTimings')}
        </CustomFormLabel>
        <CustomTextField
          id="daily_timings"
          fullWidth
          {...formik.getFieldProps('daily_timings')}
          error={formik.touched.daily_timings && Boolean(formik.errors.daily_timings)}
          helperText={formik.touched.daily_timings && formik.errors.daily_timings}
        />
      </Grid>
      <Grid item xs={12}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <CustomFormLabel htmlFor="additional_info">
            {t('TournamentsScreen.additionalInfo')}
          </CustomFormLabel>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2">Normal Editor</Typography>
            <Switch
              checked={useHtmlEditor}
              onChange={(e) => setUseHtmlEditor(e.target.checked)}
              color="primary"
            />
            <Typography variant="body2">HTML Editor</Typography>
          </Stack>
        </Stack>
        {useHtmlEditor ? (
          <Paper
            sx={{
              border: `1px solid ${borderColor}`,
              '& .rdw-editor-wrapper': {
                display: 'flex',
                flexDirection: 'column',
              },
              '& .rdw-editor-toolbar': {
                padding: '6px 5px',
                borderRadius: '2px',
                border: 'none',
                borderBottom: `1px solid ${borderColor}`,
                marginBottom: 0,
              },
              '& .rdw-editor-main': {
                minHeight: '200px',
                padding: '12px',
                fontSize: '14px',
              },
              '& .rdw-option-wrapper': {
                border: '1px solid',
                borderColor: borderColor,
                minWidth: '25px',
                height: '25px',
                '&:hover': {
                  boxShadow: '1px 1px 0px ' + borderColor,
                },
              },
              '& .rdw-option-active': {
                boxShadow: '1px 1px 0px ' + borderColor + ' inset',
              },
            }}
            variant="outlined"
          >
            <Editor
              editorState={editorState}
              onEditorStateChange={onEditorStateChange}
              placeholder={t('TournamentsScreen.additionalInfo')}
              toolbar={{
                options: ['inline', 'blockType', 'fontSize', 'list', 'textAlign', 'colorPicker', 'link', 'embedded', 'emoji', 'remove', 'history'],
                inline: {
                  options: ['bold', 'italic', 'underline', 'strikethrough', 'monospace'],
                },
                blockType: {
                  inDropdown: true,
                  options: ['Normal', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'Blockquote', 'Code'],
                },
                fontSize: {
                  options: [8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60, 72, 96],
                },
                list: {
                  options: ['unordered', 'ordered', 'indent', 'outdent'],
                },
                textAlign: {
                  options: ['left', 'center', 'right', 'justify'],
                },
                link: {
                  options: ['link', 'unlink'],
                },
              }}
            />
          </Paper>
        ) : (
          <CustomTextField
            id="additional_info"
            fullWidth
            multiline
            rows={4}
            {...formik.getFieldProps('additional_info')}
            error={formik.touched.additional_info && Boolean(formik.errors.additional_info)}
            helperText={formik.touched.additional_info && formik.errors.additional_info}
          />
        )}
      </Grid>
      {!isFederation && (
        <Grid item xs={6}>
          <CustomFormLabel htmlFor="is_live">{t('TournamentsScreen.isLive')}</CustomFormLabel>
          <FormControl>
            <RadioGroup
              row
              value={formik.values.is_live}
              onChange={(e) => formik.setFieldValue('is_live', Number(e.target.value))}
            >
              <FormControlLabel value={1} control={<CustomRadio />} label={t('Common.yes')} />
              <FormControlLabel value={0} control={<CustomRadio />} label={t('Common.no')} />
            </RadioGroup>
          </FormControl>
        </Grid>
      )}
      <Grid item xs={6}>
        <CustomFormLabel htmlFor="is_global">{t('TournamentsScreen.isGlobal')}</CustomFormLabel>
        <FormControl>
          <RadioGroup
            row
            value={formik.values.is_global}
            onChange={(e) => formik.setFieldValue('is_global', Number(e.target.value))}
          >
            <FormControlLabel value={1} control={<CustomRadio />} label={t('Common.yes')} />
            <FormControlLabel value={0} control={<CustomRadio />} label={t('Common.no')} />
          </RadioGroup>
        </FormControl>
      </Grid>
      <Grid item xs={6}>
        <CustomFormLabel htmlFor="is_private">{t('TournamentsScreen.isPrivate')}</CustomFormLabel>
        <FormControl>
          <RadioGroup
            row
            value={formik.values.is_private}
            onChange={(e) => formik.setFieldValue('is_private', Number(e.target.value))}
          >
            <FormControlLabel value={1} control={<CustomRadio />} label={t('Common.yes')} />
            <FormControlLabel value={0} control={<CustomRadio />} label={t('Common.no')} />
          </RadioGroup>
        </FormControl>
      </Grid>
      {/* waiting list */}
      <Grid item xs={6}>
        <CustomFormLabel htmlFor="is_waiting_list">
          {t('TournamentsScreen.withWaitingList')}
        </CustomFormLabel>
        <FormControl>
          <RadioGroup
            row
            value={formik.values.is_waiting_list}
            onChange={(e) => formik.setFieldValue('is_waiting_list', Number(e.target.value))}
          >
            <FormControlLabel value={1} control={<CustomRadio />} label={t('Common.yes')} />
            <FormControlLabel value={0} control={<CustomRadio />} label={t('Common.no')} />
          </RadioGroup>
        </FormControl>
      </Grid>
      {/* age_restriction */}
      <Grid item xs={6}>
        <CustomFormLabel htmlFor="age_restriction">
          {t('TournamentsScreen.ageRestriction')}
        </CustomFormLabel>
        <FormControl>
          <RadioGroup
            row
            value={formik.values.age_restriction}
            onChange={(e) => formik.setFieldValue('age_restriction', e.target.value)}
          >
            <FormControlLabel
              value={1}
              control={<CustomRadio />}
              label={t('TournamentsScreen.yes')}
            />
            <FormControlLabel
              value={0}
              control={<CustomRadio />}
              label={t('TournamentsScreen.no')}
            />
          </RadioGroup>
        </FormControl>
      </Grid>
      {/* is_golden_point */}
      {/* <Grid item xs={6}>
        <CustomFormLabel htmlFor="is_golden_point">
          {t('TournamentsScreen.withGoldenPoint')}
        </CustomFormLabel>
        <FormControl>
          <RadioGroup
            row
            value={formik.values.is_golden_point}
            onChange={(e) => formik.setFieldValue('is_golden_point', Number(e.target.value))}
          >
            <FormControlLabel value={1} control={<CustomRadio />} label={t('Common.yes')} />
            <FormControlLabel value={0} control={<CustomRadio />} label={t('Common.no')} />
          </RadioGroup>
        </FormControl>
      </Grid>
      <Grid item xs={6}>
        <CustomFormLabel htmlFor="is_third_set_tie_breaker">
          {t('TournamentsScreen.withThirdSetTieBreaker')}
        </CustomFormLabel>
        <FormControl>
          <RadioGroup
            row
            value={formik.values.is_third_set_tie_breaker}
            onChange={(e) =>
              formik.setFieldValue('is_third_set_tie_breaker', Number(e.target.value))
            }
          >
            <FormControlLabel value={1} control={<CustomRadio />} label={t('Common.yes')} />
            <FormControlLabel value={0} control={<CustomRadio />} label={t('Common.no')} />
          </RadioGroup>
        </FormControl>
      </Grid> */}
      {/* show_teams */}
      <Grid item xs={6}>
        <CustomFormLabel htmlFor="show_teams">{t('TournamentsScreen.showTeams')}</CustomFormLabel>
        <FormControl>
          <RadioGroup
            row
            value={formik.values.show_teams}
            onChange={(e) => formik.setFieldValue('show_teams', Number(e.target.value))}
          >
            <FormControlLabel value={1} control={<CustomRadio />} label={t('Common.yes')} />
            <FormControlLabel value={0} control={<CustomRadio />} label={t('Common.no')} />
          </RadioGroup>
        </FormControl>
      </Grid>
      {/* schedule_notifications */}
      <Grid item xs={6}>
        <CustomFormLabel htmlFor="schedule_notifications">
          {t('TournamentsScreen.scheduleNotifications')}
        </CustomFormLabel>
        <FormControl>
          <RadioGroup
            row
            value={formik.values.schedule_notifications}
            onChange={(e) => formik.setFieldValue('schedule_notifications', Number(e.target.value))}
          >
            <FormControlLabel value={1} control={<CustomRadio />} label={t('Common.yes')} />
            <FormControlLabel value={0} control={<CustomRadio />} label={t('Common.no')} />
          </RadioGroup>
        </FormControl>
      </Grid>
    </>
  );
};

export default AdditionalInfo;
